import { useEffect, useRef } from 'react';
import { useWebSocket } from '@/contexts/WebSocketContext';

/**
 * useNotificationRefresh
 * ----------------------
 * Listens for `notification` socket events whose popupType matches any of the
 * supplied `triggers` and calls `onRefresh()` automatically.
 *
 * Usage:
 *   useNotificationRefresh(['purchase_request_approval'], fetchData);
 *
 * The hook debounces rapid back-to-back events so the page re-fetches at most
 * once every `debounceMs` milliseconds (default 1 500 ms).
 */
const useNotificationRefresh = (triggers = [], onRefresh, debounceMs = 1500) => {
  const { socket, isConnected } = useWebSocket();
  const timerRef = useRef(null);
  const onRefreshRef = useRef(onRefresh);

  // Keep the callback ref current so the effect never needs to re-register
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleNotification = (data) => {
      const type = data?.popupType || data?.type || '';
      const matches =
        triggers.length === 0 || triggers.some((t) => type.includes(t));

      if (!matches) return;

      // Debounce: cancel pending refresh and schedule a new one
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        try {
          onRefreshRef.current?.();
        } catch (e) {
          // Never crash the listener
        }
      }, debounceMs);
    };

    socket.on('notification', handleNotification);
    return () => {
      socket.off('notification', handleNotification);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [socket, isConnected, triggers, debounceMs]);
};

export default useNotificationRefresh;
